import React, { useEffect } from 'react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { SEOHero, SEOSection, FeatureGrid, BenefitList, InternalLinkGrid, CTABanner } from '../../components/SEOPageComponents';
import { Shield, FileText, Zap, BarChart3, Receipt, Scale, Clock, CheckCircle2 } from 'lucide-react';

export default function EInvoiceMalaysia() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="e-Invoice Malaysia — Complete Guide to LHDN Digital Invoicing"
        description="Everything you need to know about e-invoice Malaysia. Learn how LHDN's MyInvois system works, compliance requirements, and how StayMii automates e-invoicing for hotels, PMS, and business systems."
        keywords="e invoice malaysia, malaysia e invoice, einvoice malaysia, e-invoice malaysia guide, lhdn e invoice malaysia, myinvois malaysia, digital invoice malaysia"
        canonicalUrl="/e-invoice-malaysia"
      />
      <div className="min-h-screen bg-white">
        <LandingNavBar />
        <SEOHero
          badge="e-Invoice Malaysia"
          h1="e-Invoice Malaysia: Automate LHDN Compliance for Your Business"
          subtitle="Malaysia's e-Invoice mandate requires all businesses to submit digital invoices via LHDN's MyInvois system. StayMii automates the entire process — from data capture to submission — for hotels, PMS, channel managers, and business systems."
        />

        <SEOSection h2="What is e-Invoice Malaysia?" description="e-Invoice Malaysia is the government's digital invoicing initiative managed by LHDN (Lembaga Hasil Dalam Negeri). Under this mandate, businesses must generate, validate, and submit invoices electronically through the MyInvois platform.">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Key Requirements</h3>
              <BenefitList items={[
                { title: 'Digital Invoice Submission', desc: 'All invoices must be submitted electronically to LHDN via the MyInvois API.' },
                { title: 'Real-Time Validation', desc: 'Each e-invoice is validated by LHDN in real-time before it becomes official.' },
                { title: 'Mandatory for All Businesses', desc: 'The mandate applies to all businesses operating in Malaysia, with phased rollout by revenue tier.' },
                { title: 'Penalties for Non-Compliance', desc: 'Businesses that fail to comply face fines of up to RM200,000 or imprisonment.' },
              ]} />
            </div>
            <div className="bg-[#0F172A] rounded-2xl p-8 border border-blue-500/20">
              <h3 className="text-lg font-bold text-white mb-4">How StayMii Helps</h3>
              <div className="space-y-4">
                {['Auto-generates e-invoices from your PMS or booking system', 'Validates each invoice against LHDN requirements', 'Submits directly to MyInvois API in real-time', 'Tracks compliance status with real-time alerts', 'Works with hotels, Airbnb, POS, and accounting systems'].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-white/80">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SEOSection>

        <SEOSection h2="e-Invoice Features for Malaysian Businesses" bg="bg-slate-50">
          <FeatureGrid items={[
            { icon: Receipt, title: 'Automated e-Invoice Generation', desc: 'Convert bookings, sales, and transactions into LHDN-compliant e-invoices automatically.' },
            { icon: Shield, title: 'LHDN MyInvois Compliant', desc: 'Every e-invoice meets LHDN specifications. Validated before submission to avoid rejections.' },
            { icon: Zap, title: 'Real-Time Submission', desc: 'Submit e-invoices to LHDN the moment they are generated. No batch delays.' },
            { icon: Scale, title: 'Audit-Ready Records', desc: 'Full audit trail for every e-invoice. Exportable reports for tax filing and compliance audits.' },
            { icon: BarChart3, title: 'Compliance Dashboard', desc: 'Monitor submission status, rejection reasons, and compliance rates in real-time.' },
            { icon: Clock, title: 'Quick Setup', desc: 'Go live in days. StayMii connects to your existing system with minimal configuration.' },
          ]} />
        </SEOSection>

        <SEOSection h2="Related e-Invoice Solutions">
          <InternalLinkGrid links={[
            { to: '/lhdn-e-invoice', title: 'LHDN e-Invoice Guide', desc: 'Detailed guide on LHDN compliance requirements' },
            { to: '/myinvois-api', title: 'MyInvois API Integration', desc: 'How StayMii connects to the MyInvois API' },
            { to: '/hotel-e-invoice-system', title: 'Hotel e-Invoice System', desc: 'e-Invoice automation for hotels and resorts' },
            { to: '/pms-e-invoice-integration', title: 'PMS e-Invoice Integration', desc: 'Connect your PMS for automated e-invoicing' },
            { to: '/channel-manager-e-invoice', title: 'Channel Manager e-Invoice', desc: 'OTA booking e-invoice automation' },
            { to: '/airbnb-e-invoice-malaysia', title: 'Airbnb e-Invoice Malaysia', desc: 'e-Invoice for Airbnb and short-term rental hosts' },
          ]} />
        </SEOSection>

        <CTABanner title="Ready to Automate Your e-Invoicing?" subtitle="Join Malaysian businesses using StayMii to stay LHDN-compliant." />
        <LandingFooter />
      </div>
    </>
  );
}
